/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { difference, intersectionWith } from 'lodash-es';
import { toast } from 'react-toastify';
import { api } from '@/api';
import { useAddLink, useChangeLink, useDeleteLink } from '@/queries/case';
import { CaseLinkType } from '@/api/apis/case';
import { retrieveUnifiedError } from '@/api/utils';

const getChangedLinks = (existingLinks: CaseLinkType[], newLinks: CaseLinkType[]) =>
  intersectionWith(newLinks, existingLinks, (l1, l2) => {
    return l1.id === l2.id && (l1.url !== l2.url || l1.description !== l2.description);
  });

const diffLinkMutations = async (
  existingLinks: CaseLinkType[],
  newLinks: CaseLinkType[],
  caseId: string,
  mutations: {
    addLink: (caseId: string, body: { url: string; description: string }) => Promise<unknown>;
    editLink: (caseId: string, linkId: string, body: { url: string; description: string }) => Promise<unknown>;
    deleteLink: (caseId: string, linkId: string) => Promise<unknown>;
  },
) => {
  const existingIds = existingLinks.map((l) => l.id);
  const newIds = newLinks.map((l) => l.id);
  const idsToAttach = difference(newIds, existingIds);
  const idsToDetach = difference(existingIds, newIds);
  const changedLinks = getChangedLinks(existingLinks, newLinks);

  try {
    for (const { id, url, description } of changedLinks) {
      await mutations.editLink(caseId, id, { description, url });
    }

    for (const id of idsToAttach) {
      const link = newLinks.find((l) => l.id === id);
      if (link) {
        await mutations.addLink(caseId, { url: link.url, description: link.description });
      }
    }

    for (const linkId of idsToDetach) {
      await mutations.deleteLink(caseId, linkId);
    }
  } catch (e) {
    const error = retrieveUnifiedError(e);
    toast.error(error.message);
  }
};

export const useUpdateLinks = () => {
  const { mutateAsync: addLink } = useAddLink();
  const { mutateAsync: editLink } = useChangeLink();
  const { mutateAsync: deleteLink } = useDeleteLink();

  return (existingLinks: CaseLinkType[], newLinks: CaseLinkType[], caseId: string) =>
    diffLinkMutations(existingLinks, newLinks, caseId, {
      addLink: (id, linkData) => addLink({ caseId: id, linkData }),
      editLink: (id, linkId, linkData) => editLink({ caseId: id, linkId, linkData }),
      deleteLink: (id, linkId) => deleteLink({ caseId: id, linkId }),
    });
};

/**
 * Same as `useUpdateLinks`, but bypasses the shared TX Center mutations — those write their
 * (TX-shaped) response into a cache slot that OPO cases share, which previously corrupted an
 * OPO case's cached data and caused a redirect loop on its details page. OPO refreshes its own
 * cache separately after this call, so skipping that write is safe.
 */
export const useUpdateOpoLinks = () => {
  return (existingLinks: CaseLinkType[], newLinks: CaseLinkType[], caseId: string) =>
    diffLinkMutations(existingLinks, newLinks, caseId, {
      addLink: (id, body) => api.case.postLink(id, body),
      editLink: (id, linkId, body) => api.case.patchLink(id, linkId, body),
      deleteLink: (id, linkId) => api.case.deleteLink(id, linkId),
    });
};
